import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddConnectionIcon } from '../../../../../../../../assets';
import { TextTooltip } from '../../../../../../../../components';
import SearchInput from '../../../../../../../../components/Input/SearchInput';
import { useSessionContext } from '../../../../../../../../contexts/SessionContext';
import { ExtendedListing } from '../../../../../../../../models/Listing';
import ZIndexLayers from '../../../../../../../../services/ZIndexLayers';
import {
  Organization,
  useConnectionsQuery,
  useCreateConnectionInviteMutation,
} from '../../../../../../../../types/api.graphql';
import { EmailRegex } from '../../../../../../../../utils/RouteHelper';
import { getInitials } from '../../../../../../../../utils/User';
import { ExtendedOrg, ShareStep } from '../../../ShareDialog';
import OrganizationDropdown from '../../common';
import OrganizationItem from './OrganizationItem';
import {
  AddConnectionButton,
  AddConnectionEmptyViewCTA,
  AddConnectionFormContainer,
  AddConnectionFormCTAsContainer,
  AddConnectionIconButton,
  AddConnectionStepContainer,
  AvatarImage,
  ConnectionLabel,
  Container,
  EmptyViewContainer,
  EmptyViewCTAsContainer,
  EmptyViewSubtitle,
  EmptyViewTextContainer,
  EmptyViewTitle,
  ListContainer,
  NoSearchResultText,
  OrganizationsWrapper,
  PaddingContainer,
  SearchInputAndButtonWrapper,
  SelectedOrgBox,
  SelectedOrgContainer,
  SelectedOrgLabel,
  ShowAboveTabletWrapper,
  ShowTabletAndBelowWrapper,
  StyledButton,
  StyledInput,
  StyledSecondaryButton,
  StyledVehicleInformation,
} from './ShareByOrganizationStep.styles';

interface ShareByOrganizationStepProps {
  listing?: ExtendedListing;
  listings?: ExtendedListing[];
  organization: ExtendedOrg;
  setOrganization: (organization: ExtendedOrg) => void;
  organizationFrom: Organization;
  setOrganizationFrom: (organization: Organization) => void;
  step: ShareStep;
  setStep: (value: ShareStep) => void;
  customVehicleInfo?: React.ReactNode;
}

export const ShareByOrganizationStep = ({
  listing,
  listings,
  organization,
  setOrganization,
  organizationFrom,
  setOrganizationFrom,
  step,
  setStep,
  customVehicleInfo,
}: ShareByOrganizationStepProps) => {
  // Dependencies
  const { me } = useSessionContext();
  const { t } = useTranslation();

  // Use first listing's org for bulk case since we validate at dialog level
  const targetListing = listing || listings?.[0];

  const availableOrgs = useMemo(() => {
    // For bulk share, only filter out receiving org since sharing org may vary
    if (listings) {
      return me?.organizations
        .filter((org) => org.id !== targetListing?.receivingOrganization?.id)
        .sort((org1, org2) => org1.name.localeCompare(org2.name))
        .map((org) => ({ ...org, isConnection: false }));
    }

    // For single share, filter both receiving and sharing orgs
    return me?.organizations
      .filter(
        (org) =>
          org.id !== targetListing?.receivingOrganization?.id && org.id !== targetListing?.sharingOrganization?.id,
      )
      .sort((org1, org2) => org1.name.localeCompare(org2.name))
      .map((org) => ({ ...org, isConnection: false }));
  }, [me?.organizations, targetListing, listings]);

  // State
  const [filteredOrganizations, setFilteredOrganizations] = useState(availableOrgs);
  const availableConnections = useRef<ExtendedOrg[]>(undefined);
  const [searchText, setSearchText] = useState('');
  const [showListHeaderBorder, setShowListHeaderBorder] = useState(false);
  const [addConnectionForm, setAddConnectionForm] = useState({
    email: '',
    connectToOrgId: organizationFrom?.id,
  });

  const areAvailableOrgs = useMemo(() => filteredOrganizations?.length > 0, [filteredOrganizations]);

  // Refs
  const listRef = useRef<HTMLDivElement>();

  // Mutations
  const [createConnection, { loading: loadingCreateConnection }] = useCreateConnectionInviteMutation();

  const userBelongsToOriginOrg = (connection): boolean => {
    return me?.organizations.some((org) => org.id === connection.originOrganizationId);
  };

  // Filter connections only to get the ones related to the listing organization
  const getConnectionsByOrganization = (organizationId, connections) => {
    return connections.filter((connection) => {
      const userBelongs = userBelongsToOriginOrg(connection);
      return userBelongs
        ? connection?.originOrganization?.id === organizationId
        : connection?.targetOrganization?.id === organizationId;
    });
  };

  const getCorrectOrganizationForEachSideOfConnection = (connection) => {
    const userBelongs = userBelongsToOriginOrg(connection);
    return userBelongs ? connection?.targetOrganization : connection?.originOrganization;
  };

  // Queries
  const { loading } = useConnectionsQuery({
    variables: {
      first: 100,
    },
    onCompleted(data) {
      const organizations = getConnectionsByOrganization(
        targetListing?.receivingOrganization?.id,
        data?.connections?.nodes,
      )?.map((connection) => getCorrectOrganizationForEachSideOfConnection(connection));

      const connectionsWithOrgs = organizations?.map((organization) => {
        return { ...organization, isConnection: true };
      });
      availableConnections.current = connectionsWithOrgs.sort((conn1, conn2) => conn1.name.localeCompare(conn2.name));
      const allOrgs = availableOrgs?.concat(connectionsWithOrgs);
      setFilteredOrganizations(allOrgs);
    },
  });

  // Handlers
  const handleOrganizationClick = (organization: Organization) => {
    setStep(ShareStep.SHARE_WITH_ORG_STEP_2);
    setOrganization(organization);
  };

  const handleOnCloseConnectionInvite = () => {
    setStep(ShareStep.SHARE_WITH_ORG_STEP_1);
  };

  const handleSendConnectionInvite = () => {
    void (async () => {
      const email = addConnectionForm.email;
      const organizationId = parseInt(addConnectionForm.connectToOrgId);
      await createConnection({
        variables: {
          input: {
            originOrganizationId: organizationId,
            targetEmail: email,
          },
        },
      });
      handleOnCloseConnectionInvite();
    })();
  };

  const handleAddConnectionClick = () => {
    setStep(ShareStep.SHARE_WITH_ORG_ADD_CONNECTION);
  };

  const handleTextChange = (text: string) => {
    // Update search text
    setSearchText(text);
    const filteredOrgs =
      text.length > 0
        ? availableOrgs
            ?.filter((org) => org.name.toLowerCase().includes(text.toLowerCase()))
            .map((org) => {
              return { ...org, isConnection: false };
            })
        : availableOrgs;
    let filteredConnections = [];
    if (availableConnections.current?.length > 0) {
      filteredConnections =
        text.length > 0
          ? availableConnections.current?.filter((org) => org.name.toLowerCase().includes(text.toLowerCase())) || []
          : availableConnections.current;
    }
    setFilteredOrganizations(filteredOrgs.concat(filteredConnections));
  };

  const handleScroll = () => {
    const scrollTop = listRef?.current?.scrollTop;
    setShowListHeaderBorder(scrollTop > 0);
  };

  // Partials
  const renderEmptyView = () => (
    <EmptyViewContainer>
      <EmptyViewTextContainer>
        <EmptyViewTitle>{t('vdp.shareModal.emptyViewTitle')}</EmptyViewTitle>
        <EmptyViewSubtitle>{t('vdp.shareModal.emptyViewSubtitle')}</EmptyViewSubtitle>
      </EmptyViewTextContainer>
      <EmptyViewCTAsContainer>
        <AddConnectionEmptyViewCTA $disableMinWidth onPress={handleAddConnectionClick}>
          {t('vdp.shareModal.addConnection')}
        </AddConnectionEmptyViewCTA>
      </EmptyViewCTAsContainer>
    </EmptyViewContainer>
  );

  const renderOrgShimmer = () => (
    <>
      <OrganizationItem loading organization={undefined} />
      <OrganizationItem loading organization={undefined} />
      <OrganizationItem loading organization={undefined} />
    </>
  );

  const renderOrgList = () => (
    <>
      {filteredOrganizations.length > 0 ? (
        <>
          {filteredOrganizations?.map((organization) => (
            <OrganizationItem
              organization={organization}
              onClick={handleOrganizationClick}
              isConnection={organization?.isConnection}
            />
          ))}
          {loading && renderOrgShimmer()}
        </>
      ) : (
        <NoSearchResultText>{t('vdp.shareModal.noResultsFound', { param: searchText })}</NoSearchResultText>
      )}
    </>
  );

  const renderSelectedOrgComponent = () => {
    return (
      <SelectedOrgContainer>
        <SelectedOrgLabel>{t('vdp.shareModal.shareWith')}</SelectedOrgLabel>
        <SelectedOrgBox>
          <AvatarImage text={getInitials(organization.name)} src={organization.imageUrl} />
          {organization.name}
          {organization?.isConnection ? <ConnectionLabel>{t('vdp.shareModal.connection')}</ConnectionLabel> : null}
        </SelectedOrgBox>
      </SelectedOrgContainer>
    );
  };

  const renderSearchInputAndConnectionButton = () => (
    <SearchInputAndButtonWrapper>
      <SearchInput
        autoHeight
        mobileTabletWidth="100%"
        placeholder={t('vdp.shareModal.searchForOrganization')}
        onTextChange={handleTextChange}
      />
      <ShowAboveTabletWrapper>
        <TextTooltip tooltipText={t('vdp.shareModal.addConnection')} $zIndex={ZIndexLayers.overModal}>
          <AddConnectionIconButton onPress={handleAddConnectionClick}>
            <AddConnectionIcon />
          </AddConnectionIconButton>
        </TextTooltip>
      </ShowAboveTabletWrapper>
      <ShowTabletAndBelowWrapper>
        <AddConnectionButton $disableMinWidth auto icon={<AddConnectionIcon />} onPress={handleAddConnectionClick}>
          {t('vdp.shareModal.addConnection')}
        </AddConnectionButton>
      </ShowTabletAndBelowWrapper>
    </SearchInputAndButtonWrapper>
  );

  const renderVehicleInformation = () => (
    <PaddingContainer>{customVehicleInfo || <StyledVehicleInformation listing={listing} />}</PaddingContainer>
  );

  // Helpers
  const shouldDisplayShareFromDropdown = () => {
    if (!targetListing?.shared || !targetListing?.receivingUser) {
      return false;
    }

    return true;
  };

  const getShareFromTooltip = () => {
    // Use different tooltip text for bulk share vs single listing
    if (listings?.length) {
      return t('vdp.shareModal.selectOrgFromTooltipBulkByOrg');
    }
    return t('vdp.shareModal.selectOrgFromTooltip');
  };

  return (
    <Container $isLastStep={!!organization}>
      {step === ShareStep.SHARE_WITH_ORG_STEP_2 ? (
        <>
          {renderVehicleInformation()}
          <OrganizationsWrapper>
            {renderSelectedOrgComponent()}
            {shouldDisplayShareFromDropdown() ? (
              <OrganizationDropdown
                availableOrganizations={me?.organizations}
                selectedOrganization={organizationFrom}
                setSelectedOrganization={setOrganizationFrom}
                disableDropdown={!!listing?.receivingOrganization}
                label={t('vdp.shareModal.shareFrom')}
                infoTooltip={getShareFromTooltip()}
              />
            ) : null}
          </OrganizationsWrapper>
        </>
      ) : step === ShareStep.SHARE_WITH_ORG_ADD_CONNECTION ? (
        <AddConnectionStepContainer>
          <AddConnectionFormContainer>
            <StyledInput
              autoComplete="email"
              id="email"
              type="email"
              autocapitalize="none"
              fullWidth
              label={t('common.email.label')}
              placeholder={t('common.email.placeholder')}
              value={addConnectionForm?.email}
              onChange={(e: any) =>
                setAddConnectionForm({
                  ...addConnectionForm,
                  email: e.target.value as string,
                })
              }
            />
            <OrganizationDropdown
              availableOrganizations={me?.organizations}
              selectedOrganization={organizationFrom}
              setSelectedOrganization={(org: Organization) =>
                setAddConnectionForm({ ...addConnectionForm, connectToOrgId: org.id })
              }
              label={t('vdp.shareModal.connectTo')}
            />
          </AddConnectionFormContainer>
          <AddConnectionFormCTAsContainer>
            <StyledSecondaryButton
              $disableMinWidth
              onPress={() => {
                // Add timeout to prevent propagating the click event
                setTimeout(() => {
                  setStep(ShareStep.SHARE_WITH_ORG_STEP_1);
                }, 50);
              }}
            >
              {t('common.cancel')}
            </StyledSecondaryButton>
            <StyledButton
              $disableMinWidth
              disabled={!addConnectionForm.email || !EmailRegex.test(addConnectionForm.email.trim())}
              loading={loadingCreateConnection}
              onPress={() => {
                // Add timeout to prevent propagating the click event
                setTimeout(() => {
                  handleSendConnectionInvite();
                }, 50);
              }}
            >
              {t('vdp.shareModal.sendInvite')}
            </StyledButton>
          </AddConnectionFormCTAsContainer>
        </AddConnectionStepContainer>
      ) : (
        <>
          {renderVehicleInformation()}
          {renderSearchInputAndConnectionButton()}
          <ListContainer onScroll={handleScroll} ref={listRef} $addBorderTop={showListHeaderBorder}>
            {!areAvailableOrgs && renderEmptyView()}
            {areAvailableOrgs && renderOrgList()}
          </ListContainer>
        </>
      )}
    </Container>
  );
};

export default ShareByOrganizationStep;
